import { WebContainer } from '@webcontainer/api';
import { WORK_DIR_NAME } from 'chef-agent/constants';
import { cleanStackTrace } from '~/utils/stacktrace';
import { createScopedLogger } from 'chef-agent/utils/logger';
import { setContainerBootState, ContainerBootState } from '~/lib/stores/containerBootState';
import { workbenchStore } from '~/lib/stores/workbench.client';
import { chooseExperience } from '~/utils/experienceChooser';
import { withResolvers } from '~/utils/promises';

interface WebContainerContext {
  loaded: boolean;
}

const webcontainerContext: WebContainerContext = import.meta.hot?.data.webcontainerContext ?? {
  loaded: false,
};

if (import.meta.hot) {
  import.meta.hot.data.webcontainerContext = webcontainerContext;
}

const logger = createScopedLogger('webcontainer');

const existingWebcontainer: Promise<WebContainer> | undefined = import.meta.hot?.data.webcontainer;
const existingResolvers: PromiseWithResolvers<WebContainer> | undefined = import.meta.hot?.data.webcontainerResolvers;

const resolvers = existingResolvers ?? withResolvers<WebContainer>();
export const webcontainer: Promise<WebContainer> = existingWebcontainer ?? resolvers.promise;

if (import.meta.hot) {
  import.meta.hot.data.webcontainerResolvers = resolvers;
}

let bootStarted = existingWebcontainer !== undefined;

export function bootWebcontainer() {
  if (bootStarted || import.meta.env.SSR) {
    return webcontainer;
  }
  const experience = chooseExperience(navigator.userAgent, window.crossOriginIsolated);
  const shouldBoot = experience === 'the-real-thing' || experience === 'mobile-warning';
  if (!shouldBoot) {
    console.error('Not attempting to boot webcontainer because window.crossOriginIsolated is not true');
    return webcontainer;
  }
  bootStarted = true;
  const booted = Promise.resolve()
    .then(() => {
      setContainerBootState(ContainerBootState.STARTING);
      return WebContainer.boot({
        coep: 'credentialless',
        workdirName: WORK_DIR_NAME,
        forwardPreviewErrors: true,
      });
    })
    .then(async (container) => {
      container.on('preview-message', (message) => {
        logger.info('WebContainer preview message:', JSON.stringify(message));

        if (message.type === 'PREVIEW_UNCAUGHT_EXCEPTION' || message.type === 'PREVIEW_UNHANDLED_REJECTION') {
          const isPromise = message.type === 'PREVIEW_UNHANDLED_REJECTION';
          workbenchStore.actionAlert.set({
            type: 'preview',
            title: isPromise ? 'Unhandled Promise Rejection' : 'Uncaught Exception',
            description: message.message,
            content: `Error occurred at ${message.pathname}${message.search}${message.hash}\nPort: ${message.port}\n\nStack trace:\n${cleanStackTrace(message.stack || '')}`,
            source: 'preview',
          });
        }
      });
      setContainerBootState(ContainerBootState.LOADING_SNAPSHOT);
      (globalThis as any).webcontainer = container;
      return container;
    });

  booted.then(resolvers.resolve, (error) => {
    setContainerBootState(ContainerBootState.ERROR, error);
    resolvers.reject(error);
  });

  if (import.meta.hot) {
    import.meta.hot.data.webcontainer = webcontainer;
  }
  return webcontainer;
}
